/** The shared fetch layer's memory (polish round two, the founder, 2026-10-16): pure logic, no React and no network, so it can be tested on
 *  its own. In plain words: the same address is fetched once at a time; an answer is kept for a short window (`CACHE_WINDOW_MS`) so a screen
 *  that mounts again (a move between areas, React's development double mount) reuses it; a write or a live note drops what it may have
 *  changed; an answer is never served to another session (the person signed in) than the one it was read for. The workspace is part of every
 *  address under `/api/v1/w/<slug>/`, so it is part of every key.
 *
 *  `read` reuses a request in flight or an answer inside the window. `refresh` is what a screen's reload asks for: a new request, unless one
 *  for the same address started a moment ago (`RELOAD_JOIN_MS`, the same live note reaching two hooks), which it joins. */
export const CACHE_WINDOW_MS = 5_000;
export const RELOAD_JOIN_MS = 250;

export type FetchCache = {
  /** an answer for `url`: the request in flight, an answer inside the window, or a new request through `load` */
  read<T>(url: string, load: () => Promise<T>): Promise<T>;
  /** a fresh answer for `url`: joins a request started within the join window, else a new request through `load` */
  refresh<T>(url: string, load: () => Promise<T>): Promise<T>;
  /** the settled answer inside the window, without asking (a screen's first render) */
  peek<T>(url: string): T | undefined;
  /** forget every answer (and request in flight) whose address matches: later reads ask again */
  drop(match: (url: string) => boolean): void;
  /** the session the next answers belong to; a change forgets everything read for another */
  setSession(session: string): void;
  /** be told when a new answer for `url` arrives (every hook on one address shows the same answer); answers the unsubscribe */
  subscribe(url: string, listener: (value: unknown) => void): () => void;
};

type Entry = { session: string; promise: Promise<unknown>; startedAt: number; settledAt: number | null; value: unknown };
export type FetchCacheOptions = { windowMs?: number; joinMs?: number; now?: () => number };

export function createFetchCache(o: FetchCacheOptions = {}): FetchCache {
  const windowMs = o.windowMs ?? CACHE_WINDOW_MS, joinMs = o.joinMs ?? RELOAD_JOIN_MS, now = o.now ?? Date.now;
  const entries = new Map<string, Entry>();
  const listeners = new Map<string, Set<(value: unknown) => void>>();
  let session = "";

  const usable = (e: Entry | undefined): e is Entry => !!e && e.session === session && (e.settledAt === null || now() - e.settledAt <= windowMs);
  const start = <T>(url: string, load: () => Promise<T>): Promise<T> => {
    const entry: Entry = { session, promise: Promise.resolve(), startedAt: now(), settledAt: null, value: undefined };
    entry.promise = load().then((value) => {
      entry.settledAt = now(); entry.value = value;
      if (entries.get(url) === entry) for (const l of listeners.get(url) ?? []) l(value);
      return value;
    }, (error: unknown) => { if (entries.get(url) === entry) entries.delete(url); throw error; }); // a failure is never kept
    entries.set(url, entry);
    return entry.promise as Promise<T>;
  };

  return {
    read(url, load) { const e = entries.get(url); return usable(e) ? (e.promise as never) : start(url, load); },
    refresh(url, load) { const e = entries.get(url); return e && e.session === session && now() - e.startedAt <= joinMs ? (e.promise as never) : start(url, load); },
    peek(url) { const e = entries.get(url); return usable(e) && e.settledAt !== null ? (e.value as never) : undefined; },
    drop(match) { for (const url of [...entries.keys()]) if (match(url)) entries.delete(url); },
    setSession(next) { if (next === session) return; session = next; entries.clear(); },
    subscribe(url, listener) {
      let set = listeners.get(url); if (!set) { set = new Set(); listeners.set(url, set); }
      set.add(listener);
      return () => { set.delete(listener); if (!set.size) listeners.delete(url); };
    },
  };
}

/** the workspace an address belongs to (`/api/v1/w/<slug>/…`), null for the person's own addresses (`/api/v1/me`, …) */
export const workspaceOf = (url: string): string | null => /^\/api\/v1\/w\/([^/?#]+)/.exec(url)?.[1] ?? null;

/** What a write may have changed: a write inside a workspace drops that workspace's answers and the person's own (a rename shows in the
 *  workspace list); any other write (signing in, an invitation, the profile) drops everything. */
export function staleAfterWrite(writeUrl: string): (url: string) => boolean {
  const slug = workspaceOf(writeUrl);
  if (!slug) return () => true;
  return (url) => { const w = workspaceOf(url); return w === null || w === slug; };
}

/** What a live note may have changed: a presence note only who is on which ticket; a read mark (rail dots, 2026-10-18) only the counts
 *  and the lists' unread dots, never a ticket's own read: the reader's open emits it while that read is still in flight, and reading the
 *  ticket again would move the mark first and lose what the open carried (the pop-up hint's "assigned to you"); any other note anything
 *  in its workspace. */
export function staleAfterNote(slug: string, kind: string): (url: string) => boolean {
  if (kind === "presence") return (url) => url.startsWith(`/api/v1/w/${slug}/presence`);
  if (kind === "ticket.read") return (url) => url.startsWith(`/api/v1/w/${slug}/counts`) || url.startsWith(`/api/v1/w/${slug}/tickets?`) || url.startsWith(`/api/v1/w/${slug}/tickets/counts`);
  return (url) => workspaceOf(url) === slug;
}
